import React, { useEffect, useRef } from 'react';
import { countryList } from '../constants/countries';
import {
  COUNTRY_TYPEAHEAD_RESET_MS,
  matchCountryTypeaheadKey,
} from '../utils/countrySelectTypeahead';

export type CountrySelectProps = {
  id?: string;
  name?: string;
  value: string;
  onChange: (country: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  'aria-invalid'?: boolean;
};

/**
 * Country select with predictable keyboard typeahead: letter keys always
 * jump to the first matching country (native select often flips first/last).
 */
export const CountrySelect: React.FC<CountrySelectProps> = ({
  id,
  name,
  value,
  onChange,
  className,
  style,
  placeholder = '– Select your country',
  disabled,
  required,
  'aria-invalid': ariaInvalid,
}) => {
  const bufferRef = useRef('');
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length !== 1 || !/[a-zA-Z]/.test(e.key)) return;

    // Stop native select typeahead (first vs last match is browser-dependent).
    e.preventDefault();

    const { nextBuffer, match } = matchCountryTypeaheadKey(bufferRef.current, e.key);
    bufferRef.current = nextBuffer;
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      bufferRef.current = '';
    }, COUNTRY_TYPEAHEAD_RESET_MS);

    if (match) onChange(match);
  };

  return (
    <select
      id={id}
      name={name}
      value={value}
      disabled={disabled}
      required={required}
      aria-invalid={ariaInvalid}
      onChange={e => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      className={className}
      style={style}
    >
      <option value="" className="text-gray-900">
        {placeholder}
      </option>
      {countryList.map(countryName => (
        <option key={countryName} value={countryName} className="text-gray-900">
          {countryName}
        </option>
      ))}
    </select>
  );
};

export default CountrySelect;
